import type { CloudConfigSnapshot } from '../domain/cloud';
import { db, initializeDatabase, type WaschPilotDatabase } from '../data/database';
import { cacheCloudConfiguration, readCloudSyncMetadata } from './cache';
import { cloudApi } from './api';
import { isCloudMode } from './mode';

export type CloudAvailability = 'local' | 'online' | 'cached' | 'defaults';

export interface CloudBootstrapState {
  mode: 'local' | 'cloud';
  availability: CloudAvailability;
  revision: number | null;
  lastSyncedAt: string | null;
}

export interface CloudConfigurationReader {
  getConfig(): Promise<CloudConfigSnapshot>;
}

export async function refreshCloudConfiguration(
  options: {
    enabled?: boolean;
    database?: WaschPilotDatabase;
    client?: CloudConfigurationReader;
  } = {},
): Promise<CloudBootstrapState> {
  const enabled = options.enabled ?? isCloudMode;
  const database = options.database ?? db;
  await initializeDatabase(database);
  if (!enabled) {
    return { mode: 'local', availability: 'local', revision: null, lastSyncedAt: null };
  }

  try {
    const snapshot = await (options.client ?? cloudApi).getConfig();
    await cacheCloudConfiguration(snapshot, database);
    const metadata = await readCloudSyncMetadata(database);
    return {
      mode: 'cloud',
      availability: 'online',
      revision: metadata?.revision ?? snapshot.revision,
      lastSyncedAt: metadata?.lastSyncedAt ?? snapshot.updatedAt,
    };
  } catch {
    const metadata = await readCloudSyncMetadata(database);
    return {
      mode: 'cloud',
      availability: metadata ? 'cached' : 'defaults',
      revision: metadata?.revision ?? null,
      lastSyncedAt: metadata?.lastSyncedAt ?? null,
    };
  }
}
